import React, { useCallback, useMemo } from 'react';
import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Heading, Select, Text, SelectOptionProps } from '@televet/kibble-ui';
import { GetClinicDirectBookingSettingsQuery } from 'shared/types/graphql';
import { UpdateDirectBookingSettingsReturnType } from './UpdateDirectBookingSettingsReturnType';
import * as Sentry from '@sentry/react';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider/context/IntegrationsProvider';
import { integrationSystemDisplayName } from 'shared/providers/IntegrationsProvider/types/IntegrationSystem';

export type StartTimeIncrementsInput = {
  directBookingSettingsData?: GetClinicDirectBookingSettingsQuery;
  updateDirectBookingSettings: UpdateDirectBookingSettingsReturnType;
  refetchSettings: () => void;
  clinicId: string | undefined;
};

const INTERVAL_OPTIONS = [
  {
    label: '10 minutes',
    value: '10',
    avimarkOnly: false,
  },
  {
    label: '15 minutes',
    value: '15',
    avimarkOnly: false,
  },
  {
    label: '20 minutes',
    value: '20',
    avimarkOnly: true,
  },
  {
    label: '30 minutes',
    value: '30',
    avimarkOnly: false,
  },
];

export function DirectBookingAppointmentIntervalInMinutes({
  directBookingSettingsData,
  updateDirectBookingSettings,
  clinicId,
  refetchSettings,
}: StartTimeIncrementsInput): JSX.Element {
  const { primaryIntegrationName } = useIntegrationsProvider();
  const isAvimark = primaryIntegrationName === integrationSystemDisplayName.AVIMARK;

  const filteredOptions = useMemo(() => {
    return INTERVAL_OPTIONS.filter((option) => {
      return isAvimark || !option.avimarkOnly;
    });
  }, [isAvimark]);

  const handelIncrementChanged = useCallback(
    async (option: SelectOptionProps) => {
      const appointmentIntervalInMinutesDisplay = +(option?.value || '');
      if (
        clinicId &&
        appointmentIntervalInMinutesDisplay &&
        directBookingSettingsData?.findFirstClinicDirectBookingSetting?.appointmentIntervalInMinutesDisplay !==
          appointmentIntervalInMinutesDisplay
      ) {
        await updateDirectBookingSettings({
          variables: {
            where: { id: directBookingSettingsData?.findFirstClinicDirectBookingSetting?.id || '' },
            update: {
              appointmentIntervalInMinutesDisplay,
              appointmentIntervalInMinutes: appointmentIntervalInMinutesDisplay,
            },
            create: {
              appointmentIntervalInMinutesDisplay,
              appointmentIntervalInMinutes: appointmentIntervalInMinutesDisplay,
              clinic: {
                connect: { id: clinicId },
              },
            },
          },
        }).catch((err) => {
          console.error(err);
          Sentry.captureException(err);
        });
        refetchSettings();
      }
    },
    [directBookingSettingsData, clinicId, updateDirectBookingSettings, refetchSettings],
  );

  const appointmentIntervalInMinutes = useMemo(() => {
    return (
      directBookingSettingsData?.findFirstClinicDirectBookingSetting?.appointmentIntervalInMinutesDisplay?.toString() ||
      ''
    );
  }, [directBookingSettingsData]);

  return (
    <Flex mt={5} direction="column" w="100%" mb={5}>
      <Heading size="md">Start Time Increments</Heading>
      <Box mt={5} mb={5} maxW="550px">
        <Text size="sm" variant="subtle">
          Control which appointment start times are offered when clients directly book appointments. For example, if you
          set the start time increments to 30 minutes, clients can book appointments that start at 2 PM, 2:30 PM, 3 PM,
          and 3:30 PM.
        </Text>
      </Box>
      <Box maxW="550px">
        <Select
          label=""
          maxW="240px"
          options={filteredOptions}
          size="md"
          value={appointmentIntervalInMinutes}
          listProps={{ onSelect: handelIncrementChanged }}
        />
      </Box>
    </Flex>
  );
}
